import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, X, Check, Loader2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../../i18n';
import { LoadingSpinner } from '../common/Badges';
import { Logo } from '../common/Logo';
import { ITEM_ICONS, CATEGORY_COLORS } from '../../utils/blueprintConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicQuotePage = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [propertyType, setPropertyType] = useState('residential');
  const [selectedCategory, setSelectedCategory] = useState('electrical');
  const [items, setItems] = useState([]);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '', project_name: '', notes: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/services/catalog`);
        const data = await res.json();
        setCatalog(data.services || data);
      } catch { toast.error('Failed to load services'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const addItem = (category, key, service) => {
    const existing = items.find(i => i.key === key && i.category === category);
    if (existing) {
      setItems(items.map(i => i.key === key && i.category === category ? { ...i, qty: i.qty + 1 } : i));
    } else {
      setItems([...items, { category, key, name: service.description, qty: 1 }]);
    }
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const submitQuote = async () => {
    if (!contactInfo.email) { toast.error('Ju lutem shkruani email-in tuaj'); return; }
    if (!contactInfo.name) { toast.error('Ju lutem shkruani emrin tuaj'); return; }
    if (items.length === 0) { toast.error('Zgjidhni te pakten nje sherbim'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/quote/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactInfo, items })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Oferta juaj u dergua me sukses! Do t\'ju kontaktojme se shpejti.');
      setSent(true);
    } catch { toast.error('Deshtoi dergimi'); }
    finally { setSending(false); }
  };

  if (loading) return <LoadingSpinner />;
  if (!catalog) return null;

  if (sent) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">{t('quote_sent_title')}</h2>
          <p className="text-gray-400 mb-6">{t('quote_sent_sub')}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/login')} className="btn-primary px-6 py-2.5" data-testid="create-account-after-quote">{t('quote_create_account')}</button>
            <button onClick={() => navigate('/')} className="btn-secondary px-6 py-2.5">{t('quote_go_back')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="public-quote-page">
      <div className="bg-gray-950 border-b border-gray-800 px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/"><Logo /></Link>
          <div className="h-6 w-px bg-gray-800"></div>
          <h1 className="text-lg font-bold text-white">{t('quote_title')}</h1>
        </div>
        <button onClick={() => navigate('/login')} className="text-sm text-red-500 hover:text-red-400 font-medium">{t('login_or_register')}</button>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">{t('quote_select_services')}</h2>
            <p className="text-gray-400 text-sm">{t('quote_select_sub')}</p>
          </div>
          <div className="flex gap-2">
            {['residential', 'commercial'].map(tp => (
              <button key={tp} onClick={() => setPropertyType(tp)} className={`px-4 py-2 rounded-lg text-sm font-medium ${propertyType === tp ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400'}`}>
                {tp === 'residential' ? 'Rezidenciale' : 'Komerciale'}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(catalog).map(([key, cat]) => {
              const colors = CATEGORY_COLORS[key] || CATEGORY_COLORS.electrical;
              return (
                <button key={key} onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedCategory === key ? `${colors.bg} ${colors.border} ${colors.text} border` : 'bg-gray-800 text-gray-400'}`}>
                  {cat.name}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {catalog[selectedCategory] && Object.entries(catalog[selectedCategory][propertyType] || catalog[selectedCategory].residential || {}).map(([key, service]) => {
              const IconComp = ITEM_ICONS[key] || Wrench;
              return (
                <button key={key} onClick={() => addItem(selectedCategory, key, service)}
                  className="text-left p-4 bg-gray-900 border border-gray-800 rounded-lg hover:border-red-500/40 transition-all group" data-testid={`quote-service-${key}`}>
                  <div className="flex items-center gap-3">
                    <IconComp className="w-5 h-5 text-gray-500 group-hover:text-red-500 transition-colors" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{service.description}</p>
                      <p className="text-xs text-gray-600 mt-0.5">Per {service.unit}</p>
                    </div>
                    <Plus className="w-4 h-4 text-gray-600 group-hover:text-red-500" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 sticky top-4">
            <h3 className="text-lg font-bold text-white mb-4">{t('quote_your_quote')}</h3>
            {items.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">{t('quote_select_from_list')}</p>
            ) : (
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-800 rounded p-2.5">
                    <div className="flex-1">
                      <p className="text-sm text-white">{item.name}</p>
                      <p className="text-xs text-gray-500">x{item.qty}</p>
                    </div>
                    <button onClick={() => removeItem(idx)} className="text-gray-500 hover:text-red-500 p-1"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-gray-800 pt-4 space-y-3">
              <h4 className="text-sm font-bold text-white">{t('quote_your_info')}</h4>
              <input type="text" placeholder={t('quote_your_name')} className="input w-full text-sm" value={contactInfo.name} onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })} data-testid="quote-name" required />
              <input type="email" placeholder={t('email') + ' *'} className="input w-full text-sm" value={contactInfo.email} onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })} data-testid="quote-email" required />
              <input type="tel" placeholder={t('phone')} className="input w-full text-sm" value={contactInfo.phone} onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })} data-testid="quote-phone" />
              <input type="text" placeholder={t('quote_project_name')} className="input w-full text-sm" value={contactInfo.project_name} onChange={(e) => setContactInfo({ ...contactInfo, project_name: e.target.value })} data-testid="quote-project" />
              <textarea placeholder={t('quote_notes')} className="input w-full text-sm min-h-[60px]" value={contactInfo.notes} onChange={(e) => setContactInfo({ ...contactInfo, notes: e.target.value })} data-testid="quote-notes" />
              <button onClick={submitQuote} disabled={sending || items.length === 0} className="btn-primary w-full py-3 font-bold disabled:opacity-50" data-testid="submit-quote-btn">
                {sending && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
                {t('quote_send')} ({items.length} {t('quote_services')})
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-600 text-center">{t('quote_after_sub')}</p>
        </div>
      </div>
    </div>
  );
};

export default PublicQuotePage;
